import React, { useMemo, useState, Component } from 'react';
import { useRaceStore } from '../../stores/raceStore';
import { useTranslation } from '../../utils/i18n';
import { MapPinIcon, FlagTriangleRightIcon, ChevronDownIcon, Trash2Icon, PlusIcon, RouteIcon, TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, ShieldAlertIcon, TentIcon, DropletIcon, UtensilsIcon, CircleDotIcon, TimerIcon } from 'lucide-react';
import { CheckpointType, TerrainType, Segment } from '../../types';
import { Button } from '../ui/Button';
// --- Constants & Helpers ---
const CHECKPOINT_CONFIG: Record<CheckpointType, {
  color: string;
  bg: string;
  border: string;
  icon: React.ElementType;
  labelKey: string;
}> = {
  None: {
    color: '#9B9BA7',
    bg: 'bg-[#F4F4F5]',
    border: 'border-[#E4E4E7]',
    icon: CircleDotIcon,
    labelKey: 'checkpoint.None'
  },
  BV: {
    color: '#0666EB',
    bg: 'bg-[#EFF6FF]',
    border: 'border-[#DBEAFE]',
    icon: TentIcon,
    labelKey: 'checkpoint.BV'
  },
  Aid: {
    color: '#00B843',
    bg: 'bg-[#ECFDF5]',
    border: 'border-[#D1FAE5]',
    icon: UtensilsIcon,
    labelKey: 'checkpoint.Aid'
  },
  Water: {
    color: '#0EA5E9',
    bg: 'bg-[#F0F9FF]',
    border: 'border-[#E0F2FE]',
    icon: DropletIcon,
    labelKey: 'checkpoint.Water'
  },
  Other: {
    color: '#F59E0B',
    bg: 'bg-[#FFFBEB]',
    border: 'border-[#FEF3C7]',
    icon: MapPinIcon,
    labelKey: 'checkpoint.Other'
  }
};
const TERRAIN_KEYS: TerrainType[] = ['Road', 'SmoothTrail', 'MixedTrail', 'Rocky', 'Technical', 'MudSand', 'OfftrailSteep'];
const CHECKPOINT_KEYS: CheckpointType[] = ['None', 'BV', 'Aid', 'Water', 'Other'];
const TERRAIN_COLORS: Record<TerrainType, string> = {
  Road: '#6B6B76',
  SmoothTrail: '#00B843',
  MixedTrail: '#FF9500',
  Rocky: '#E34402',
  Technical: '#C93D00',
  MudSand: '#8B6914',
  OfftrailSteep: '#7B61FF'
};
function formatCutoff(minutes?: number): string {
  if (!minutes && minutes !== 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}
// --- Components ---
function CheckpointsSummaryBar({
  segments
}: {
  segments: Segment[];
}) {
  const stats = useMemo(() => {
    let aidCount = 0;
    let waterCount = 0;
    let bvCount = 0;
    segments.forEach(s => {
      if (s.checkpointType === 'Aid') aidCount++;
      if (s.checkpointType === 'Water') waterCount++;
      if (s.checkpointType === 'BV') bvCount++;
    });
    const lastSegment = segments[segments.length - 1];
    const finalCutoff = lastSegment?.cutoffMinutes ? formatCutoff(lastSegment.cutoffMinutes) : null;
    return {
      aidCount,
      waterCount,
      bvCount,
      finalCutoff
    };
  }, [segments]);
  return <div className="flex items-center gap-2 flex-wrap px-1" data-id="element-348">
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]" data-id="element-349">
        <MapPinIcon className="w-3 h-3" data-id="element-350" />
        <span data-id="element-351">{segments.length} segments</span>
      </div>
      {stats.aidCount > 0 && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#ECFDF5] text-[#047857] border-[#D1FAE5]" data-id="element-352">
          <UtensilsIcon className="w-3 h-3" data-id="element-353" />
          <span data-id="element-354">{stats.aidCount} aid</span>
        </div>}
      {stats.waterCount > 0 && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#F0F9FF] text-[#0369A1] border-[#E0F2FE]" data-id="element-355">
          <DropletIcon className="w-3 h-3" data-id="element-356" />
          <span data-id="element-357">{stats.waterCount} water</span>
        </div>}
      {stats.bvCount > 0 && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#EFF6FF] text-[#1D4ED8] border-[#DBEAFE]" data-id="element-358">
          <TentIcon className="w-3 h-3" data-id="element-359" />
          <span data-id="element-360">{stats.bvCount} base camp</span>
        </div>}
      {stats.finalCutoff && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold bg-[#FEF2F2] text-[#E34402] border-[#FECACA]" data-id="element-361">
          <ShieldAlertIcon className="w-3 h-3" data-id="element-362" />
          <span data-id="element-363">{stats.finalCutoff}</span>
        </div>}
    </div>;
}
function CheckpointRow({
  segment,
  index,
  isLast,
  accumulatedDistance,
  isExpanded,
  onClick
}: {
  segment: Segment;
  index: number;
  isLast: boolean;
  accumulatedDistance: number;
  isExpanded: boolean;
  onClick: () => void;
}) {
  const {
    t
  } = useTranslation();
  const config = CHECKPOINT_CONFIG[segment.checkpointType] || CHECKPOINT_CONFIG.None;
  const Icon = config.icon;
  const hasCutoff = segment.cutoffMinutes && segment.cutoffMinutes > 0;
  return <button onClick={onClick} className={`w-full bg-white border p-3 lg:p-4 flex items-start lg:items-center gap-3 lg:gap-4 hover:border-[#FC4C02]/30 transition-all text-left group shadow-sm ${isExpanded ? 'rounded-t-xl border-[#FC4C02]/20 border-b-0 z-10 relative' : 'rounded-xl border-[#E8E8ED]'} ${isLast ? 'bg-gradient-to-r from-[#FFFBEB]/40 to-transparent' : ''}`} data-id="element-364">
      {/* Index / Icon */}
      <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors border mt-0.5 lg:mt-0 ${isExpanded ? 'bg-[#FC4C02] border-[#FC4C02] text-white' : isLast ? 'bg-[#FC4C02]/10 border-[#FC4C02]/20 text-[#FC4C02]' : 'bg-[#F7F7FA] border-[#E8E8ED] text-[#6B6B76] group-hover:border-[#FC4C02]/30'}`} data-id="element-365">
        {isLast ? <FlagTriangleRightIcon className="w-4 h-4 lg:w-5 lg:h-5" data-id="element-366" /> : <span className="text-xs lg:text-sm font-bold" data-id="element-367">{index}</span>}
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0" data-id="element-368">
        {/* Name + chevron (mobile) */}
        <div className="flex items-center justify-between gap-2 mb-1" data-id="element-369">
          <span className={`text-[13px] lg:text-sm font-semibold text-[#2D2D2D] truncate ${isLast ? 'font-bold' : ''}`} data-id="element-370">
            {segment.name}
          </span>

          {/* Desktop tags */}
          <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0" data-id="element-371">
            {segment.checkpointType !== 'None' && <div className={`px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 ${config.bg} ${config.border}`} data-id="element-372">
                <Icon className="w-3 h-3" style={{
              color: config.color
            }} data-id="element-373" />
                <span style={{
              color: config.color
            }} data-id="element-374">
                  {t(config.labelKey)}
                </span>
              </div>}
            <div className="px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 bg-[#F7F7FA] border-[#E8E8ED]" data-id="element-375">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
              backgroundColor: TERRAIN_COLORS[segment.terrainType || 'SmoothTrail']
            }} data-id="element-376" />
              <span className="text-[#6B6B76]" data-id="element-377">
                {t(`terrain.${segment.terrainType || 'SmoothTrail'}`)}
              </span>
            </div>
          </div>

          {/* Mobile chevron */}
          <div className={`lg:hidden transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-180 text-[#FC4C02]' : 'text-[#D4D4D8]'}`} data-id="element-378">
            <ChevronDownIcon className="w-4 h-4" data-id="element-379" />
          </div>
        </div>

        {/* Mobile tags row */}
        <div className="flex lg:hidden items-center gap-1.5 mb-1.5 flex-wrap" data-id="element-380">
          {segment.checkpointType !== 'None' && <div className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border flex items-center gap-0.5 ${config.bg} ${config.border}`} data-id="element-381">
              <Icon className="w-2.5 h-2.5" style={{
            color: config.color
          }} data-id="element-382" />
              <span style={{
            color: config.color
          }} data-id="element-383">
                {t(config.labelKey)}
              </span>
            </div>}
          <div className="px-1.5 py-0.5 rounded-full text-[9px] font-bold border flex items-center gap-0.5 bg-[#F7F7FA] border-[#E8E8ED]" data-id="element-384">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
            backgroundColor: TERRAIN_COLORS[segment.terrainType || 'SmoothTrail']
          }} data-id="element-385" />
            <span className="text-[#6B6B76]" data-id="element-386">
              {t(`terrain.${segment.terrainType || 'SmoothTrail'}`)}
            </span>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between gap-2" data-id="element-387">
          <div className="flex items-center gap-2 lg:gap-3 text-[11px] lg:text-xs text-[#9B9BA7] min-w-0" data-id="element-388">
            <span className="flex items-center gap-0.5 lg:gap-1 font-medium text-[#6B6B76] whitespace-nowrap" data-id="element-389">
              <RouteIcon className="w-3 h-3 flex-shrink-0" data-id="element-390" />
              {segment.distance.toFixed(1)}km
            </span>
            <span className="w-0.5 h-3 bg-[#E8E8ED] flex-shrink-0" data-id="element-391" />
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-392">
              <TrendingUpIcon className="w-3 h-3 flex-shrink-0" data-id="element-393" />+
              {Math.round(segment.elevationGain)}m
            </span>
            <span className="flex items-center gap-0.5 whitespace-nowrap" data-id="element-394">
              <TrendingDownIcon className="w-3 h-3 flex-shrink-0" data-id="element-395" />-
              {Math.round(segment.elevationLoss)}m
            </span>
            <span className="hidden sm:inline-flex items-center" data-id="element-396">
              <span className="w-0.5 h-3 bg-[#E8E8ED] mr-2" data-id="element-397" />
              <span className="tabular-nums whitespace-nowrap" data-id="element-398">
                @ {accumulatedDistance.toFixed(1)}km
              </span>
            </span>
          </div>

          {/* Mobile cutoff */}
          {hasCutoff && <div className={`lg:hidden flex items-center gap-1 flex-shrink-0 ${isLast ? 'text-[#E34402]' : 'text-[#FF9500]'}`} data-id="element-399">
              {isLast ? <ShieldAlertIcon className="w-2.5 h-2.5" data-id="element-400" /> : <AlertTriangleIcon className="w-2.5 h-2.5" data-id="element-401" />}
              <span className="text-[10px] font-bold tabular-nums" data-id="element-402">
                {formatCutoff(segment.cutoffMinutes)}
              </span>
            </div>}
        </div>
      </div>

      {/* Desktop Right Side: Cutoff & Chevron */}
      <div className="hidden lg:flex items-center gap-3 pl-2 border-l border-[#F0F0F5]" data-id="element-403">
        {hasCutoff && <div className={`flex flex-col items-end ${isLast ? 'text-[#E34402]' : 'text-[#FF9500]'}`} data-id="element-404">
            <div className="flex items-center gap-1" data-id="element-405">
              {isLast ? <ShieldAlertIcon className="w-3 h-3" data-id="element-406" /> : <AlertTriangleIcon className="w-3 h-3" data-id="element-407" />}
              <span className="text-xs font-bold tabular-nums" data-id="element-408">
                {formatCutoff(segment.cutoffMinutes)}
              </span>
            </div>
          </div>}
        <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180 text-[#FC4C02]' : 'text-[#D4D4D8] group-hover:text-[#FC4C02]'}`} data-id="element-409">
          <ChevronDownIcon className="w-5 h-5" data-id="element-410" />
        </div>
      </div>
    </button>;
}
function CheckpointDetailPanel({
  segment,
  isLast,
  onClose
}: {
  segment: Segment;
  isLast: boolean;
  onClose: () => void;
}) {
  const {
    t
  } = useTranslation();
  const {
    updateSegment,
    removeSegment,
    segments
  } = useRaceStore();
  const [cutoffHours, setCutoffHours] = useState(segment.cutoffMinutes ? Math.floor(segment.cutoffMinutes / 60).toString() : '');
  const [cutoffMinutes, setCutoffMinutes] = useState(segment.cutoffMinutes ? (segment.cutoffMinutes % 60).toString() : '');
  const handleCutoffChange = (h: string, m: string) => {
    setCutoffHours(h);
    setCutoffMinutes(m);
    const hours = parseInt(h) || 0;
    const mins = parseInt(m) || 0;
    const totalMins = hours * 60 + mins;
    updateSegment(segment.id, {
      cutoffMinutes: totalMins > 0 ? totalMins : undefined
    });
  };
  return <div className="bg-[#FAFAFA] rounded-b-xl border border-t-0 border-[#E8E8ED] -mt-1 overflow-hidden animate-in slide-in-from-top-2 duration-200" data-id="element-411">
      <div className="p-4 space-y-4" data-id="element-412">
        {/* Name Input */}
        <div data-id="element-413">
          <label className="block text-[10px] font-bold text-[#9B9BA7] uppercase tracking-wider mb-1.5" data-id="element-414">
            {t('segments.name')}
          </label>
          <input type="text" value={segment.name} onChange={e => updateSegment(segment.id, {
          name: e.target.value
        })} className="w-full h-10 px-3 bg-white border border-[#E8E8ED] rounded-xl text-sm font-medium text-[#2D2D2D] focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent transition-all" placeholder="Checkpoint Name" data-id="element-415" />
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-416">
          {/* Type Selector */}
          <div data-id="element-417">
            <label className="block text-[10px] font-bold text-[#9B9BA7] uppercase tracking-wider mb-1.5" data-id="element-418">
              {t('segments.type')}
            </label>
            <div className="relative" data-id="element-419">
              <select value={segment.checkpointType} onChange={e => updateSegment(segment.id, {
              checkpointType: e.target.value as CheckpointType
            })} className="w-full h-10 pl-3 pr-8 bg-white border border-[#E8E8ED] rounded-xl text-sm font-medium text-[#2D2D2D] focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent appearance-none transition-all cursor-pointer" data-id="element-420">
                {CHECKPOINT_KEYS.map(key => <option key={key} value={key} data-id="element-421">
                    {t(`checkpoint.${key}`)}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B9BA7] pointer-events-none" data-id="element-422" />
            </div>
          </div>

          {/* Terrain Selector */}
          <div data-id="element-423">
            <label className="block text-[10px] font-bold text-[#9B9BA7] uppercase tracking-wider mb-1.5" data-id="element-424">
              {t('segments.terrain')}
            </label>
            <div className="relative" data-id="element-425">
              <select value={segment.terrainType || 'SmoothTrail'} onChange={e => updateSegment(segment.id, {
              terrainType: e.target.value as TerrainType
            })} className="w-full h-10 pl-3 pr-8 bg-white border border-[#E8E8ED] rounded-xl text-sm font-medium text-[#2D2D2D] focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent appearance-none transition-all cursor-pointer" data-id="element-426">
                {TERRAIN_KEYS.map(key => <option key={key} value={key} data-id="element-427">
                    {t(`terrain.${key}`)}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B9BA7] pointer-events-none" data-id="element-428" />
            </div>
          </div>
        </div>

        {/* Cutoff & Actions */}
        <div className="flex items-end gap-4" data-id="element-429">
          <div className="flex-1" data-id="element-430">
            <label className="block text-[10px] font-bold text-[#9B9BA7] uppercase tracking-wider mb-1.5 flex items-center gap-2" data-id="element-431">
              {isLast ? 'Final Cutoff' : t('segments.cutoff')}
              {isLast && <ShieldAlertIcon className="w-3 h-3 text-[#E34402]" data-id="element-432" />}
            </label>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${isLast ? 'bg-[#FEF2F2] border-[#FECACA]' : 'bg-white border-[#E8E8ED]'}`} data-id="element-433">
              <TimerIcon className={`w-4 h-4 ${isLast ? 'text-[#E34402]' : 'text-[#9B9BA7]'}`} data-id="element-434" />
              <input type="number" min="0" max="99" value={cutoffHours} onChange={e => handleCutoffChange(e.target.value, cutoffMinutes)} placeholder="--" className="w-8 bg-transparent border-none p-0 text-sm font-bold text-center focus:ring-0 placeholder:text-[#D4D4D8]" data-id="element-435" />
              <span className="text-xs font-medium text-[#9B9BA7]" data-id="element-436">h</span>
              <input type="number" min="0" max="59" value={cutoffMinutes} onChange={e => handleCutoffChange(cutoffHours, e.target.value)} placeholder="--" className="w-8 bg-transparent border-none p-0 text-sm font-bold text-center focus:ring-0 placeholder:text-[#D4D4D8]" data-id="element-437" />
              <span className="text-xs font-medium text-[#9B9BA7]" data-id="element-438">m</span>
            </div>
          </div>

          {segments.length > 1 && !isLast && <Button variant="ghost" onClick={() => {
          removeSegment(segment.id);
          onClose();
        }} className="text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEF2F2]" data-id="element-439">
              <Trash2Icon className="w-4 h-4 mr-2" data-id="element-440" />
              Remove
            </Button>}
        </div>
      </div>
    </div>;
}
// --- Main Component ---
export function SegmentsTable({
  onAdd,
  title
}: {
  onAdd?: () => void;
  title?: string;
}) {
  const {
    t
  } = useTranslation();
  const {
    segments
  } = useRaceStore();
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const accumulatedDistances = useMemo(() => {
    return segments.reduce<number[]>((acc, seg, i) => {
      const prev = i > 0 ? acc[i - 1] : 0;
      acc.push(prev + seg.distance);
      return acc;
    }, []);
  }, [segments]);
  if (segments.length === 0) {
    return <div className="p-12 text-center bg-white rounded-2xl border border-[#E8E8ED] shadow-sm" data-id="element-441">
        <MapPinIcon className="h-8 w-8 text-[#E8E8ED] mx-auto mb-3" data-id="element-442" />
        <p className="text-[#6B6B76] font-medium" data-id="element-443">
          {t('segments.noCheckpoints')}
        </p>
        <p className="text-[#9B9BA7] text-sm mt-1" data-id="element-444">
          {t('segments.noCheckpointsDesc')}
        </p>
        {onAdd && <Button onClick={onAdd} className="mt-4" data-id="element-445">
            <PlusIcon className="w-4 h-4 mr-2" data-id="element-446" />
            {t('course.add')}
          </Button>}
      </div>;
  }
  const handleToggle = (id: string) => {
    setSelectedSegmentId(prev => prev === id ? null : id);
  };
  return <div className="space-y-6 pb-20" data-id="element-447">
      {/* List */}
      <div className="space-y-3" data-id="element-448">
        <div className="flex items-center justify-between gap-3" data-id="element-449">
          {/* Inline Summary Stats */}
          <div className="flex-1 min-w-0" data-id="element-450">
            <CheckpointsSummaryBar segments={segments} data-id="element-451" />
          </div>
          {onAdd && <button onClick={onAdd} className="text-xs font-bold text-[#FC4C02] hover:text-[#E34402] flex items-center gap-1 transition-colors flex-shrink-0" data-id="element-452">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-453" />
              {t('course.add')}
            </button>}
        </div>

        {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        const isExpanded = selectedSegmentId === segment.id;
        return <div key={segment.id} data-id="element-454">
              <CheckpointRow segment={segment} index={index + 1} isLast={isLast} accumulatedDistance={accumulatedDistances[index]} isExpanded={isExpanded} onClick={() => handleToggle(segment.id)} data-id="element-455" />
              {isExpanded && <CheckpointDetailPanel segment={segment} isLast={isLast} onClose={() => setSelectedSegmentId(null)} data-id="element-456" />}
            </div>;
      })}
      </div>

      {/* Bottom Add Button (for convenience on long lists) */}
      {onAdd && segments.length > 3 && <Button variant="secondary" className="w-full border-dashed border-[#E8E8ED] bg-[#FAFAFA] hover:bg-[#F4F4F5] text-[#9B9BA7] hover:text-[#6B6B76]" onClick={onAdd} data-id="element-457">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-458" />
          {t('course.add')}
        </Button>}
    </div>;
}